import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  AutoFixHigh,
  ExpandMore,
  Person,
  Groups,
  ArrowRight,
  FindReplace,
  Refresh,
} from "@mui/icons-material";
import { LoadingOperation } from "./types";
import { ConfirmDialog } from "../../../shared/ui";
import { OfficerProfile } from "../../../shared/interfaces/OfficerProfile";

interface AIAssistanceToolsProps {
  loadingOperation: LoadingOperation;
  disabled: boolean;
  onToFirstPerson: () => void;
  onToThirdPerson: (officerName: string) => void;
  onCorrectName: (nameToCorrect: string, correctName: string) => void;
  onRegenerateWithContext: (context: string) => void;
  userProfile?: OfficerProfile | null;
}

export const AIAssistanceTools: React.FC<AIAssistanceToolsProps> = ({
  loadingOperation,
  disabled,
  onToFirstPerson,
  onToThirdPerson,
  onCorrectName,
  onRegenerateWithContext,
  userProfile,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [nameToCorrect, setNameToCorrect] = useState("");
  const [correctName, setCorrectName] = useState("");
  const [additionalContext, setAdditionalContext] = useState("");
  const [officerNameDialogOpen, setOfficerNameDialogOpen] = useState(false);
  const [manualOfficerName, setManualOfficerName] = useState("");

  const disableButtons = loadingOperation !== null || disabled;

  const handleCorrectNameClick = () => {
    if (nameToCorrect.trim() && correctName.trim()) {
      onCorrectName(nameToCorrect, correctName);
      setNameToCorrect("");
      setCorrectName("");
    }
  };

  const handleRegenerateClick = () => {
    if (additionalContext.trim()) {
      onRegenerateWithContext(additionalContext);
      setAdditionalContext("");
    }
  };

  /**
   * Formats officer name for 3rd person narrative (e.g., "Officer Miller", "Sergeant Smith")
   */
  const getFormattedOfficerName = (
    profile: OfficerProfile | null | undefined,
    manualName?: string
  ): string | null => {
    if (manualName?.trim()) {
      return manualName.trim();
    }
    if (profile?.rank && profile?.lastName) {
      return `${profile.rank} ${profile.lastName}`;
    }
    return null;
  };

  const handleToThirdPersonClick = () => {
    const officerName = getFormattedOfficerName(userProfile);

    if (!officerName) {
      // Open dialog to get officer name manually
      setOfficerNameDialogOpen(true);
      return;
    }
    onToThirdPerson(officerName);
  };

  const handleOfficerNameSubmit = () => {
    if (manualOfficerName.trim()) {
      setOfficerNameDialogOpen(false);
      onToThirdPerson(manualOfficerName.trim());
      setManualOfficerName("");
    }
  };

  return (
    <Box>
      <Accordion
        variant="outlined"
        defaultExpanded={true}
        sx={{
          borderRadius: 3,
          background: isDark
            ? theme.palette.background.paper
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 50%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
          border: "2px solid transparent",
          backgroundClip: "padding-box",
          position: "relative",
          "&:before": { display: "none" },
          overflow: "hidden",
          boxShadow: isDark
            ? `0 4px 20px ${alpha(theme.palette.primary.main, 0.2)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.15)}`
            : `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}, 0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            boxShadow: isDark
              ? `0 6px 24px ${alpha(theme.palette.primary.main, 0.25)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`
              : `0 6px 24px ${alpha(theme.palette.primary.main, 0.2)}, 0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore sx={{ color: "primary.main" }} />}
          sx={{
            background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.light, 0.08)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            minHeight: 56,
            "&.Mui-expanded": {
              minHeight: 56,
            },
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: "50%",
                bgcolor: "primary.main",
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              <AutoFixHigh sx={{ color: "white", fontSize: 18 }} />
            </Box>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="primary.main"
              sx={{ letterSpacing: "0.02em" }}
            >
              AI Assistance Tools
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2.5, pb: 2.5 }}>
          <Stack spacing={2.5}>
            {/* Style Adjustment */}
            <Box>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ color: "primary.dark", letterSpacing: "0.03em" }}
                gutterBottom
              >
                Refine Narrative Style
              </Typography>
              {/* Perspective conversion buttons */}
              <Stack direction="row" spacing={1} mt={0.5} justifyContent="center">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Person />}
                  disabled={disableButtons}
                  onClick={onToFirstPerson}
                  loading={loadingOperation === "toFirstPerson"}
                >
                  To 1st Person
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Groups />}
                  disabled={disableButtons}
                  onClick={handleToThirdPersonClick}
                  loading={loadingOperation === "toThirdPerson"}
                >
                  To 3rd Person
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.15) }} />

            {/* Name Correction */}
            <Box>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ color: "primary.dark", letterSpacing: "0.03em" }}
              >
                Find & Replace Name
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="center"
                mt={0.5}
              >
                <TextField
                  label="Find"
                  placeholder="Name to correct"
                  onChange={(e) => setNameToCorrect(e.target.value)}
                  value={nameToCorrect}
                  disabled={disableButtons}
                  size="small"
                  fullWidth
                />
                <ArrowRight
                  color="action"
                  sx={{ transform: "translateY(-8px)" }}
                />
                <TextField
                  label="Replace"
                  placeholder="Correct name"
                  onChange={(e) => setCorrectName(e.target.value)}
                  value={correctName}
                  disabled={disableButtons}
                  size="small"
                  fullWidth
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<FindReplace />}
                  disabled={
                    disableButtons ||
                    !nameToCorrect.trim() ||
                    !correctName.trim()
                  }
                  onClick={handleCorrectNameClick}
                  sx={{
                    minWidth: "fit-content",
                    transform: "translateY(-8px)",
                  }}
                >
                  Replace
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.15) }} />

            {/* Additional Context */}
            <Box>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ color: "primary.dark", letterSpacing: "0.03em" }}
                gutterBottom
              >
                Regenerate with Context
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                <TextField
                  fullWidth
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  value={additionalContext}
                  label="Additional Context"
                  placeholder="Add instructions or details..."
                  multiline
                  size="small"
                  disabled={disableButtons}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Refresh />}
                  disabled={disableButtons || !additionalContext.trim()}
                  onClick={handleRegenerateClick}
                  loading={loadingOperation === "addContext"}
                  sx={{
                    minWidth: "fit-content",
                    transform: "translateY(-8px)",
                  }}
                >
                  Regenerate
                </Button>
              </Stack>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Officer Name Dialog */}
      <ConfirmDialog
        open={officerNameDialogOpen}
        onClose={() => {
          setOfficerNameDialogOpen(false);
          setManualOfficerName("");
        }}
        onConfirm={handleOfficerNameSubmit}
        tone="primary"
        icon={<Person />}
        title="Enter Officer Name"
        description={`Please enter the officer's name for third-person perspective conversion (e.g., "Officer Miller", "Sergeant Smith").`}
        confirmLabel="Continue"
        confirmDisabled={!manualOfficerName.trim()}
        confirmAutoFocus={false}
      >
        <TextField
          autoFocus
          fullWidth
          sx={{ mt: 2 }}
          label="Officer Name"
          placeholder="Officer Miller"
          value={manualOfficerName}
          onChange={(e) => setManualOfficerName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && manualOfficerName.trim()) {
              handleOfficerNameSubmit();
            }
          }}
        />
      </ConfirmDialog>
    </Box>
  );
};
